/**
 * AccountingExportPage (#1039)
 *
 * Build, preview, run and track accounting export batches (CSV). Shows what the
 * next batch would contain (eligible, not-yet-exported invoices), lets the user
 * create a batch, and lists past batches with a CSV download.
 *
 * Optional issue-date window (#2884) is sent to both preview and create. Empty
 * dates mean every unexported eligible invoice.
 */

import { useMemo, useState } from 'react';
import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Typography, Button, Paper, Stack, Divider, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Chip,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import {
  useExportPreview, useExportBatches, useCreateExportBatch, downloadExportCsv,
  type ExportBatchListItem,
  type InvoiceExportFilters,
} from '@/hooks/useInvoices';
import { useNotify } from '@/hooks/useNotify';
import { translateBillingError } from '@/lib/billingErrors';
import { formatDateTime } from '@/utils/locale';


const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  building: 'info', completed: 'success', failed: 'error',
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local calendar date as YYYY-MM-DD (matches invoice-exports filters). */
function toIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** First and last local calendar days of the month at `monthOffset` from today. */
function monthRange(monthOffset: number): { dateFrom: string; dateTo: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + monthOffset + 1, 0);
  return { dateFrom: toIsoDate(start), dateTo: toIsoDate(end) };
}

function toExportFilters(dateFrom: string, dateTo: string): InvoiceExportFilters {
  return {
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  };
}

export default function AccountingExportPage() {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const exportFilters = useMemo(() => toExportFilters(dateFrom, dateTo), [dateFrom, dateTo]);
  const dateRangeInvalid = Boolean(dateFrom && dateTo && dateFrom > dateTo);
  const preview = useExportPreview(exportFilters);
  const batches = useExportBatches();
  const createBatch = useCreateExportBatch();

  const applyMonthPreset = (monthOffset: number) => {
    const range = monthRange(monthOffset);
    setDateFrom(range.dateFrom);
    setDateTo(range.dateTo);
  };

  const periodValue = dateFrom || dateTo
    ? t('export.selectedPeriod', { from: dateFrom, to: dateTo })
    : t('export.allUnexported');

  const handleCreate = async () => {
    if (dateRangeInvalid) return;
    try {
      const result = await createBatch.mutateAsync(exportFilters);
      notify.success(t('export.created', { count: result.invoiceCount }));
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  const handleDownload = async (batchId: string) => {
    try {
      await downloadExportCsv(batchId);
    } catch (err) {
      notify.error(tc('error'), translateBillingError(err, t));
    }
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }} size="small">
        {t('detailPage.backToList')}
      </Button>
      <Typography variant="h4" fontWeight={600} gutterBottom>{t('export.title')}</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>{t('export.subtitle')}</Typography>

      {/* Next batch preview */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('export.previewTitle')}</Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ sm: 'center' }}
          flexWrap="wrap"
          sx={{ mb: 2 }}
        >
          <TextField
            type="date"
            size="small"
            label={t('export.dateFrom')}
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            error={dateRangeInvalid}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { 'aria-label': t('export.dateFrom') },
            }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            type="date"
            size="small"
            label={t('export.dateTo')}
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            error={dateRangeInvalid}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { 'aria-label': t('export.dateTo') },
            }}
            sx={{ minWidth: 160 }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="outlined" size="small" onClick={() => applyMonthPreset(-1)}>
              {t('export.previousMonth')}
            </Button>
            <Button variant="outlined" size="small" onClick={() => applyMonthPreset(0)}>
              {t('export.currentMonth')}
            </Button>
          </Stack>
        </Stack>
        {dateRangeInvalid && (
          <Alert severity="error" sx={{ mb: 2 }}>{t('export.dateRangeInvalid')}</Alert>
        )}
        {preview.isPending ? (
          <CircularProgress size={24} />
        ) : preview.data ? (
          <Stack spacing={1}>
            <Row label={t('export.period')} value={periodValue} />
            <Row label={t('export.invoiceCount')} value={String(preview.data.invoiceCount)} />
            <Row label={t('detail.subtotal')} value={formatPrice(preview.data.totalNetCents)} />
            <Row label={t('export.totalTax')} value={formatPrice(preview.data.totalTaxCents)} />
            <Row label={t('detail.total')} value={formatPrice(preview.data.totalGrossCents)} bold />
            <Divider sx={{ my: 1 }} />
            <Box>
              <Button
                variant="contained"
                onClick={() => void handleCreate()}
                disabled={createBatch.isPending || dateRangeInvalid || preview.data.invoiceCount === 0}
              >
                {t('export.create')}
              </Button>
            </Box>
          </Stack>
        ) : null}
      </Paper>

      {/* Batch history */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t('export.historyTitle')}</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('export.batchDate')}</TableCell>
                <TableCell align="right">{t('export.invoiceCount')}</TableCell>
                <TableCell align="right">{t('detail.total')}</TableCell>
                <TableCell>{t('columns.status')}</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {(batches.data?.data ?? []).length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>{t('export.noBatches')}</TableCell></TableRow>
              ) : (
                (batches.data?.data ?? []).map((b: ExportBatchListItem) => (
                  <TableRow key={b.id}>
                    <TableCell>{formatDateTime(b.createdAt)}</TableCell>
                    <TableCell align="right">{b.invoiceCount}</TableCell>
                    <TableCell align="right">{formatPrice(b.totalGrossCents)}</TableCell>
                    <TableCell><Chip size="small" color={STATUS_COLOR[b.status] ?? 'default'} label={t(`export.status.${b.status}`)} /></TableCell>
                    <TableCell align="right">
                      {b.status === 'completed' && (
                        <Button size="small" startIcon={<DownloadIcon />} onClick={() => void handleDownload(b.id)}>
                          {tc('download')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

function Row({ label, value, bold = false }: { label: string; value: string; bold?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" fontWeight={bold ? 700 : 400}>{label}</Typography>
      <Typography variant="body2" fontWeight={bold ? 700 : 500}>{value}</Typography>
    </Box>
  );
}
